import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumEprMaterialType } from "./enums/enumEprMaterialType.generated";
import { EnumEprPackagingActivity } from "./enums/enumEprPackagingActivity.generated";
import { EnumEprPackagingClass } from "./enums/enumEprPackagingClass.generated";
import { EnumEprPackagingType } from "./enums/enumEprPackagingType.generated";
import { EnumEprRecyclabilityAssessmentRating } from "./enums/enumEprRecyclabilityAssessmentRating.generated";

// Model Objects

export class BomHeader extends ModelObject<BomHeader> {

    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    public version: number = 0;
    public name: string = "";
    /**
    * The category that describes how a packaging item is introduced to the UK market.
    */
    public packagingActivity: EnumEprPackagingActivity = EnumEprPackagingActivity.suppliedUnderYourBrand;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.version = jsonSerializedObject.version;
          this.name = jsonSerializedObject.name;
          this.packagingActivity = EnumEprPackagingActivity.getByValue(jsonSerializedObject.packagingActivity, this.packagingActivity);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.version = this.version;
        toRet.name = this.name;
        toRet.packagingActivity = this.packagingActivity.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "BomHeader.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
    ];
}


export class BomLine extends ModelObject<BomLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Bom Header this line belongs to.
    */
    public bomId: Guid = Guid.createEmpty();
    /**
    * Name of the packaging
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    /**
    * Recyclability assessment methodology rating for this packaging
    */
    public recyclabilityAssessmentRating: EnumEprRecyclabilityAssessmentRating = EnumEprRecyclabilityAssessmentRating.red;
    /**
    * Required when material type is either plastic or other.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public materialSubtype: string = "";
    /**
    * Packaging class of this line.
    */
    public materialType: EnumEprMaterialType = EnumEprMaterialType.aluminium;
    /**
    * Describes the purpose of the packaging.
    */
    public packagingClass: EnumEprPackagingClass = EnumEprPackagingClass.primary;
    /**
    * Packaging type of this line.
    */
    public packagingType: EnumEprPackagingType = EnumEprPackagingType.commonPublicBin;
    /**
    * Required if packaging type is drinks container, otherwise 0.
    */
    public quantityOfDrinksContainers: number = 0;
    /**
    * Weight in kilograms of packaging material.
    */
    public weightKg: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.bomId = Guid.parseOrEmpty(jsonSerializedObject.bomId);
          this.name = jsonSerializedObject.name;
          this.recyclabilityAssessmentRating = EnumEprRecyclabilityAssessmentRating.getByValue(jsonSerializedObject.recyclabilityAssessmentRating, this.recyclabilityAssessmentRating);
          this.materialSubtype = jsonSerializedObject.materialSubtype;
          this.materialType = EnumEprMaterialType.getByValue(jsonSerializedObject.materialType, this.materialType);
          this.packagingClass = EnumEprPackagingClass.getByValue(jsonSerializedObject.packagingClass, this.packagingClass);
          this.packagingType = EnumEprPackagingType.getByValue(jsonSerializedObject.packagingType, this.packagingType);
          this.quantityOfDrinksContainers = jsonSerializedObject.quantityOfDrinksContainers;
          this.weightKg = jsonSerializedObject.weightKg;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.bomId = this.bomId.toString();
        toRet.name = this.name;
        toRet.recyclabilityAssessmentRating = this.recyclabilityAssessmentRating.value;
        toRet.materialSubtype = this.materialSubtype;
        toRet.materialType = this.materialType.value;
        toRet.packagingClass = this.packagingClass.value;
        toRet.packagingType = this.packagingType.value;
        toRet.quantityOfDrinksContainers = this.quantityOfDrinksContainers;
        toRet.weightKg = this.weightKg;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "BomLine.Id cannot be empty"},
            {field: "bomId", rule: (v: any) => !v.isEmpty() || "BomLine.BomId cannot be empty"},
    ];
}


export class EprBom extends ModelObject<EprBom> {

    public header: BomHeader = new BomHeader();
    public constituents: (BomLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new BomHeader(jsonSerializedObject.header);
          this.constituents = jsonSerializedObject.constituents.map((v: any) => new BomLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.constituents = this.constituents.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "constituents", rule: (v: any) => !!v || "constituents is required"},
            {field: "constituents", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BomLine[]).reduce((acc: Array<string>, curr: BomLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class EprBomService extends ServiceBase {

  public async getEprBom(bomId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EprBom> {
    const req = {
    tenantId:api.tenantId.toString(),
    bomId:bomId.toString(),
    };
    const extractor = (response:any) => new EprBom(response.eprBom);;
    return this.makeJsonRequest("eprBom/v1/getEprBom", req, false, extractor, opts);
  };

  public async getEprBoms(bomIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EprBom)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    bomIds:bomIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.eprBom.map((v: any) => new EprBom(v));;
    return this.makeJsonRequest("eprBom/v1/getEprBoms", req, false, extractor, opts);
  };

  public async getAllEprBoms(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EprBom)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.eprBoms.map((v: any) => new EprBom(v));;
    return this.makeJsonRequest("eprBom/v1/getAllEprBoms", req, false, extractor, opts);
  };

  public async saveEprBom(eprBom: EprBom, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    eprBom:eprBom.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("eprBom/v1/saveEprBom", req, true, extractor, opts);
  };

  public async setEprBomIsHidden(bomId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    bomId:bomId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("eprBom/v1/setEprBomIsHidden", req, true, extractor, opts);
  };
}

export const eprBomService = new EprBomService();